import IconPencilSolid from '@/assets/icons/font_awesome/pencil-solid.svg?react';
import { ICON__check } from '@/assets/icons_fa';
import { ENUM__html_ref_stub } from '@/enums/ENUM__html_ref_stub';
import { Key } from '@/enums/key';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_ref_with_fallback } from '@/hooks/_base/use_ref_with_fallback';
import { use_state_ref } from '@/hooks/_base/use_state_ref';
import { use_hint_shortcut } from '@/hooks/hints_shortcut/use_hint_shortcut';
import { cn } from '@/utils/utils_client';
import { EShortcutType } from '@shared/dto/hint_shortcuts/e_shortcut_type';
import { format_compact_number } from '@shared/utils/utils_number';
import { ViewShortcutTrigger } from '../shortcut_hint/ViewShortcutTrigger';

interface Props {
  type?: 'text' | 'password';

  is_first_focus?: boolean;
  use_value_default_for_blank?: boolean;
  is_format_compact_number?: boolean;

  className?: string;

  disabled?: boolean;

  value_default: string;
  value: string;
  onChange: (value: string) => void;
  onChangeCommited: (value: string) => void;
  placeholder?: string;
}

export function InputWithButtonEdit({
  type = 'text',

  placeholder = '',

  use_value_default_for_blank = true,
  is_first_focus = false,
  is_format_compact_number = false,

  className = '',

  disabled = false,

  value_default,
  value,
  onChange,
  onChangeCommited,
}: Props) {
  /*
   * FIELDS
   */

  const [is_focused, set_is_focused, is_focused_ref] = use_state_ref(false);

  const ref_input = use_ref_with_fallback<HTMLInputElement>(ENUM__html_ref_stub.INPUT);

  const { on_user_uses_component, on_user_uses_shortcut } = use_hint_shortcut(EShortcutType.INPUT_WITH_BUTTON_EDIT_END);

  /*
   * EFFECTS
   */

  use_effect(() => {
    if (!is_focused) return;

    ref_input.current.focus();
  }, [is_focused]);

  use_effect(() => {
    if (!is_first_focus) return;

    ref_input.current.focus();

    set_is_focused(true);
  }, [is_first_focus]);

  /*
   * EVENTS
   */

  function onChangeInternal(e: React.ChangeEvent<HTMLInputElement>) {
    onChange(e.target.value);
  }

  function onButtonSaveClicked(e: React.MouseEvent<SVGSVGElement>) {
    if (disabled) return;

    e.preventDefault();
    e.stopPropagation();

    onEnterPressedInternal();
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (disabled) return;

    if (event.key == Key.Escape) onBlurInternal(null, true, true);

    if (event.key == Key.Enter) onEnterPressedInternal();
  }

  function onFocusInternal() {
    if (disabled) return;

    const { length } = ref_input.current.value;

    ref_input.current.setSelectionRange(length, length);
  }

  function onBlurInternal(e: React.FocusEvent<HTMLInputElement> | null = null, commit_value = true, used_hotkey = false) {
    if (disabled) return;
    if (is_focused_ref.current === false) return;

    // Prevent handling if this is a recursive call
    if (e && e.target !== e.currentTarget) return;

    used_hotkey ? on_user_uses_shortcut() : on_user_uses_component();

    set_is_focused(false);

    e?.stopPropagation();

    // Only programmatically blur if this isn't already a blur event
    ref_input.current.blur();

    //clear all selection
    ref_input.current.setSelectionRange(0, 0);

    //clear all selection
    window.getSelection()?.removeAllRanges();

    if (commit_value) {
      onChangeCommited(value_default);
    }
  }

  function onEnterPressedInternal() {
    if (disabled) return;

    onBlurInternal(null, false, true);

    let value_trimmed = value.trim();

    if (use_value_default_for_blank && value_trimmed.length === 0) {
      value_trimmed = value_default;
    }

    onChangeCommited(value_trimmed);
  }

  /*
   * METHODS
   */

  function format_value(value_to_format: string) {
    if (!is_format_compact_number) return value_to_format;

    return format_compact_number(value_to_format);
  }

  /*
   * RENDERS
   */

  function render_icon_edit() {
    if (is_focused) return null;

    return <IconPencilSolid className={`!w-3 !h-3 ${!disabled ? 'cursor-pointer' : ''}`} onClick={() => !disabled && set_is_focused(true)} />;
  }

  function render_icon_save() {
    if (!is_focused) return null;

    return <ICON__check className={`!size-3 ${!disabled ? 'cursor-pointer' : ''}`} onMouseDown={onButtonSaveClicked} />;
  }

  /*
   * HTML
   */

  return (
    <ViewShortcutTrigger hint_shortcut_keys={[EShortcutType.INPUT_WITH_BUTTON_EDIT_END]}>
      <div
        className={cn(
          'w-56 border border-component-border !rounded-md flex items-center justify-start text-component-item-foreground rounded-lg  hover-animation text-sm text-left px-2 bg-component cursor-default',
          is_focused ? 'list-item-selected bg-component-item-selected' : `${!disabled ? 'hover:list-item-selected' : ''}`,
          disabled ? 'opacity-50' : '',
          `${className}`,
        )}
      >
        <div className="flex justify-between items-center w-full pr-1" onClick={() => !disabled && set_is_focused(true)}>
          <input
            onFocus={onFocusInternal}
            onBlur={onBlurInternal}
            readOnly={!is_focused}
            ref={ref_input}
            value={is_focused ? value : format_value(value)}
            className={`w-full bg-transparent cursor-text placeholder-component-item-foreground font-normal focus:outline-none focus:font-normal 
            ${is_focused ? '' : 'pointer-events-none'}`}
            onChange={onChangeInternal}
            onClick={() => !disabled && set_is_focused(true)}
            {...{ disabled, placeholder, type, onKeyDown }}
          />
        </div>
        <div className="flex gap-2 items-center flex-shrink-0">
          {render_icon_edit()}
          {render_icon_save()}
        </div>
      </div>
    </ViewShortcutTrigger>
  );
}
